import React, { useEffect, useState } from 'react';
import { FiZap } from 'react-icons/fi';
import Select from 'react-select';
import services from '../../services';
import AppSelect from '../../common/AppSelect';

// "When this task is checked": move the ticket to a board / list and/or give it a property status.
// value = { boardId, listId, customProperty, customField: [fieldId] } | undefined
const selectStyle = { height: '34px', fontSize: '12.5px', minWidth: '170px' };
const multiStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '34px',
        minWidth: '200px',
        borderRadius: '8px',
        fontSize: '12.5px',
        borderColor: state.isFocused ? '#1a7d3e' : '#d1fae5',
        boxShadow: 'none',
    }),
    menuPortal: (base) => ({ ...base, zIndex: 100000 }),
    multiValue: (base) => ({ ...base, backgroundColor: '#e8f5e9', borderRadius: '6px' }),
    multiValueLabel: (base) => ({ ...base, color: '#1a7d3e', fontWeight: 600 }),
    indicatorSeparator: () => ({ display: 'none' }),
};

const fieldLabel = (item) => {
    const cf = item?.customField ?? {};
    const getLabel = (v) => (typeof v === 'object' && v !== null ? v.label ?? v.name ?? v.value ?? '' : v ?? '');
    const value = cf.value;
    const label = Array.isArray(value) ? value.map(getLabel).join(', ') : getLabel(value);
    return label || cf.name || 'Unnamed';
};

const fieldIdsOf = (value) =>
    (Array.isArray(value?.customField) ? value.customField : []).map((f) => (f && typeof f === 'object' ? f.id : f)).filter(Boolean);

export const hasChecklistItemAction = (value) =>
    !!(value && (value.listId || (value.customProperty && fieldIdsOf(value).length)));

const ChecklistItemAction = ({ value, onChange, boardOptions, ruleBoardId, ruleListId }) => {
    const [open, setOpen] = useState(hasChecklistItemAction(value));
    const [lists, setLists] = useState([]);
    const [allowedIds, setAllowedIds] = useState(null);
    const [properties, setProperties] = useState([]);
    const [fields, setFields] = useState([]);

    const boardId = value?.boardId || '';
    const propertyBoardId = boardId || ruleBoardId;
    const set = (patch) => onChange({ ...(value || {}), ...patch });

    // Lists of the target board (+ which ones its ticket process allows)
    useEffect(() => {
        setLists([]);
        setAllowedIds(null);
        if (!open || !boardId) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await services.board.SELECTED_BOARD(boardId);
                if (cancelled) return;
                setLists(
                    (data || [])
                        .slice()
                        .sort((a, b) => Number(a.sequence) - Number(b.sequence))
                        .map((l) => ({ value: l.id, label: l.coloum })),
                );
                const sameBoard = boardId === ruleBoardId;
                const byBoard = await services.automations.LIST_PROCESS_ALLOWED(
                    sameBoard ? { fromBoardId: ruleBoardId, fromListId: ruleListId } : {},
                );
                if (!cancelled) setAllowedIds(byBoard?.[boardId]?.allowedListIds || null);
            } catch (e) {
                console.error(e);
            }
        })();
        return () => { cancelled = true; };
    }, [open, boardId, ruleBoardId, ruleListId]);

    // Properties of the board the ticket will be on
    useEffect(() => {
        setProperties([]);
        if (!open || !propertyBoardId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await services.property.GET_PROPERTY_BY_BOARD_ID(propertyBoardId);
                if (!cancelled) setProperties((res?.data?.data || []).map((p) => ({ label: p.property?.propertyName, value: p.id })));
            } catch (e) {
                console.error(e);
            }
        })();
        return () => { cancelled = true; };
    }, [open, propertyBoardId]);

    // Statuses of the chosen property
    useEffect(() => {
        setFields([]);
        if (!open || !value?.customProperty) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await services.customfield.GET_CUSTOM_FIELD_BY_TASK_ID(value.customProperty);
                if (!cancelled) {
                    setFields((res?.data?.data || []).map((item) => ({ label: fieldLabel(item), value: item?.customField?.id ?? item?.id })));
                }
            } catch (e) {
                console.error(e);
            }
        })();
        return () => { cancelled = true; };
    }, [open, value?.customProperty]);

    const listOptions = allowedIds ? lists.filter((o) => o.value === value?.listId || allowedIds.includes(o.value)) : lists;
    const selectedFieldIds = fieldIdsOf(value);
    const active = hasChecklistItemAction(value);

    return (
        <>
            <button
                type="button"
                title="Move the ticket and/or set a property when this task is checked"
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-1 text-xs"
                style={{
                    whiteSpace: 'nowrap', flexShrink: 0, cursor: 'pointer', border: '1px solid',
                    borderColor: active ? '#1a7d3e' : '#e5e7eb', color: active ? '#1a7d3e' : '#6b7280',
                    background: active ? '#f0fdf4' : '#fff', borderRadius: '6px', padding: '3px 7px',
                }}
            >
                <FiZap size={12} />
                When checked
            </button>
            {open && (
                <div
                    className="flex items-center gap-2 flex-wrap"
                    style={{ flexBasis: '100%', order: 99, margin: '2px 0 4px 24px', padding: '8px 10px', background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: '8px' }}
                >
                    <span className="text-xs text-gray-600">When checked, move the ticket to</span>
                    <AppSelect
                        portal
                        value={boardId}
                        onChange={(e) => onChange({ ...(value || {}), boardId: e.target.value, listId: '', customProperty: '', customField: [] })}
                        placeholder="Board (optional)"
                        style={selectStyle}
                        options={boardOptions || []}
                    />
                    <AppSelect
                        portal
                        value={value?.listId || ''}
                        onChange={(e) => set({ listId: e.target.value })}
                        placeholder={boardId ? 'List' : 'Select a board first'}
                        disabled={!boardId}
                        style={selectStyle}
                        options={listOptions}
                    />
                    <span className="text-xs text-gray-600">and set</span>
                    <AppSelect
                        portal
                        value={value?.customProperty || ''}
                        onChange={(e) => set({ customProperty: e.target.value, customField: [] })}
                        placeholder="Property (optional)"
                        style={selectStyle}
                        options={properties}
                    />
                    <Select
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        options={fields}
                        value={fields.filter((o) => selectedFieldIds.includes(o.value))}
                        onChange={(selected) => set({ customField: (selected || []).map((o) => o.value) })}
                        placeholder={value?.customProperty ? 'Status' : 'Select a property first'}
                        isDisabled={!value?.customProperty}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={multiStyles}
                    />
                    {(boardId || value?.customProperty) && (
                        <button
                            type="button"
                            onClick={() => onChange(undefined)}
                            style={{ fontSize: '12px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                            Clear
                        </button>
                    )}
                    {allowedIds && (
                        <div style={{ flexBasis: '100%', fontSize: '11.5px', color: '#6b7280' }}>
                            This board has a ticket process: only the lists a ticket may go to are shown.
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default ChecklistItemAction;
